export type DropdownPlacement = 'auto' | 'top' | 'bottom';
export type DropdownAlign = 'start' | 'end';

export type DropdownPositionOptions = {
  trigger: HTMLElement;
  panel: HTMLElement;
  placement?: DropdownPlacement;
  align?: DropdownAlign;
  offset?: number;
  edgeMargin?: number;
  /** When true, panel is at least as wide as the trigger and can grow to fit content. */
  matchTriggerWidth?: boolean;
  zIndex?: number;
};

export type DropdownPositionStyle = Record<string, string>;

export function computeDropdownPosition(options: DropdownPositionOptions): DropdownPositionStyle {
  const {
    trigger,
    panel,
    placement = 'auto',
    align = 'start',
    offset = 4,
    edgeMargin = 8,
    matchTriggerWidth = true,
    zIndex = 2600
  } = options;

  const triggerRect = trigger.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Measure natural content width without the previous fixed width constraint.
  const previousWidth = panel.style.width;
  const previousMinWidth = panel.style.minWidth;
  panel.style.width = 'max-content';
  panel.style.minWidth = matchTriggerWidth ? `${Math.round(triggerRect.width)}px` : '';
  const measuredWidth = Math.max(
    panel.scrollWidth || 0,
    panel.offsetWidth || 0,
    matchTriggerWidth ? triggerRect.width : 0
  );
  panel.style.width = previousWidth;
  panel.style.minWidth = previousMinWidth;

  const panelWidth = Math.min(measuredWidth, viewportWidth - edgeMargin * 2);
  const panelHeight = panel.offsetHeight || panel.getBoundingClientRect().height || 0;

  const preferredBottom = triggerRect.bottom + offset;
  const preferredTop = triggerRect.top - panelHeight - offset;
  const spaceBelow = viewportHeight - triggerRect.bottom - edgeMargin;
  const spaceAbove = triggerRect.top - edgeMargin;

  let placeAbove = false;
  if (placement === 'top') {
    placeAbove = true;
  } else if (placement === 'bottom') {
    placeAbove = false;
  } else {
    placeAbove = spaceBelow < panelHeight + offset && spaceAbove > spaceBelow;
  }

  let top = placeAbove ? preferredTop : preferredBottom;
  const maxTop = Math.max(edgeMargin, viewportHeight - panelHeight - edgeMargin);
  top = Math.min(Math.max(edgeMargin, top), maxTop);

  const preferredLeft = align === 'end'
    ? triggerRect.right - panelWidth
    : triggerRect.left;
  const maxLeft = Math.max(edgeMargin, viewportWidth - panelWidth - edgeMargin);
  const left = Math.min(Math.max(edgeMargin, preferredLeft), maxLeft);

  const availableHeight = placeAbove
    ? Math.max(80, triggerRect.top - edgeMargin - offset)
    : Math.max(80, viewportHeight - triggerRect.bottom - edgeMargin - offset);

  const style: DropdownPositionStyle = {
    position: 'fixed',
    top: `${Math.round(top)}px`,
    left: `${Math.round(left)}px`,
    right: 'auto',
    bottom: 'auto',
    width: 'max-content',
    maxWidth: `calc(100vw - ${edgeMargin * 2}px)`,
    maxHeight: `${Math.round(availableHeight)}px`,
    overflowX: 'hidden',
    overflowY: 'auto',
    zIndex: String(zIndex)
  };

  if (matchTriggerWidth) {
    style.minWidth = `${Math.round(triggerRect.width)}px`;
  }

  return style;
}
